import { match } from 'ts-pattern'
import type { FeedbackScope } from '../../utils/api-client/api-schemas.ts'
import { pluralize } from '../../utils/text-utils.ts'
import { SegmentedControl, type SegmentedControlOption } from '../input/segmented-control.tsx'
import * as styles from './feedback-scope-selector.css.ts'

/** Why Project is unavailable: the one reason, shared by the help line and the option's description. */
export const UNGROUPED_REASON = "This repository isn't in a project yet."

export type FeedbackScopeSelectorProps = {
  value: FeedbackScope
  onChange: (value: FeedbackScope) => unknown
  /** The repository the result belongs to; its name anchors the repository-level reach. */
  repositoryName: string
  /** The repository's project, when it has one. Without it the project level cannot be chosen. */
  project?: { name: string; repositoryCount?: number }
  /** How many repositories the organization has, for the organization-level reach. */
  organizationRepositoryCount?: number
  /**
   * Whether Organization is on offer at all. Admins only: `useIsAdmin() === false` and the unknown
   * `undefined` both hide it, and so does a 403 that took the role away mid-session.
   */
  isOrganizationOffered: boolean
  /**
   * Set once a submission came back refused because the repository is in no project — membership
   * changed under the open modal. Project goes dead for the rest of this attempt.
   */
  isProjectRefused?: boolean
}

/**
 * Where a piece of feedback applies: the whole organization, the repository's project, or the one
 * repository (the default). The help line spells out the blast radius of the current choice.
 */
export const FeedbackScopeSelector: React.FC<FeedbackScopeSelectorProps> = ({
  value,
  onChange,
  repositoryName,
  project,
  organizationRepositoryCount,
  isOrganizationOffered,
  isProjectRefused = false,
}) => {
  const isProjectDisabled = project === undefined || isProjectRefused
  const reach = match(value)
    .with('organization', () => (
      <>
        all{' '}
        <b className={styles.helpSubject}>
          {organizationRepositoryCount ?? '—'}{' '}
          {pluralize(organizationRepositoryCount ?? 0, 'repository', 'repositories')}
        </b>{' '}
        in your organization
      </>
    ))
    .with('project', () =>
      project ? (
        <>
          the{' '}
          <b className={styles.helpSubject}>
            {project.repositoryCount ?? '—'} {pluralize(project.repositoryCount ?? 0, 'repository', 'repositories')}
          </b>{' '}
          in <b className={styles.helpSubject}>{project.name}</b>
        </>
      ) : (
        '—'
      )
    )
    .with('repository', () => (
      <>
        <b className={styles.helpSubject}>{repositoryName}</b> only
      </>
    ))
    .exhaustive()

  return (
    <div className={styles.container}>
      <p className={styles.eyebrow}>This feedback applies to</p>
      <SegmentedControl<FeedbackScope>
        ariaLabel="Feedback scope"
        value={value}
        onChange={onChange}
        options={[
          ...(isOrganizationOffered
            ? [{ value: 'organization', label: 'Organization' } satisfies SegmentedControlOption<FeedbackScope>]
            : []),
          {
            value: 'project',
            label: 'Project',
            disabled: isProjectDisabled,
            disabledReason: isProjectDisabled ? UNGROUPED_REASON : undefined,
          },
          { value: 'repository', label: 'Repository' },
        ]}
      />
      <p className={styles.help}>
        Applies to {reach}.{isProjectDisabled && ` ${UNGROUPED_REASON}`}
      </p>
    </div>
  )
}
